import { ComponentElement, ReactElement, ReactNode } from 'react';
import { PropTypes } from 'typed/imports';
import { ComponentEventHandler, ComponentEventHandler1, PureComponent, ReactEventHandler } from 'typed/components/utils';
import { default as FormattedResource, Resource, isResource, resourcePropType } from '../FormattedResource/FormattedResource';
import { DropPanel, Popup } from 'components/common';
import { addEvent, isOverEvent, removeEvent, toArray } from 'typed/utils';
import common from '!!less-vars-loader!static/common.less';
import './NestedMenu.less';

type Child =
    | undefined | null
    | ComponentElement<ItemProps, Item>
    | ReactElement;

type Children = Child | Child[] | (Child | Child[])[];


interface ItemProps {
    className?: string;
    title: ReactElement | Resource;
    collapsable?: boolean;
    expanded?: boolean;
    children?: Children;
    onExpand?: ComponentEventHandler;
}

export class Item extends PureComponent<ItemProps> {
    static readonly displayName = 'NestedMenu.Item';

    render(): ReactElement {
        throw new Error('NestedMenu.Item shouldn\'t be rendered. NestedMenu should use the InternalItem that isn\'t visible to components outside.');
    }
}


interface InternalItemProps extends ItemProps {
    id: string | number;
    componentIdentifier: number;
    depth: number;
    expanded: boolean;
    useInlineNesting: boolean;
    onItemExpand: ComponentEventHandler1<string | number>;
    onSetMaxNesting: ComponentEventHandler1<number>;
    onSecondaryDropdownClose: ComponentEventHandler;
}

interface InternalItemState {
    expandPanelBounds: { top: number; left: number; maxHeight: number; } | null;
}

class InternalItem extends PureComponent<InternalItemProps, InternalItemState> {
    static readonly propTypes = {
        componentIdentifier: PropTypes.number,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        depth: PropTypes.number,
        expanded: PropTypes.bool,
        onItemExpand: PropTypes.func,
        onSetMaxNesting: PropTypes.func,
        onSecondaryDropdownClose: PropTypes.func,

        title: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        className: PropTypes.string,
        collapsable: PropTypes.bool,
        onExpand: PropTypes.func
    };

    static readonly defaultProps = {
        collapsable: false
    };

    state: InternalItemState = {
        expandPanelBounds: null
    };


    componentDidMount() {
        this.componentDidUpdate();
    }

    componentDidUpdate() {
        if (this.props.collapsable && this.props.expanded) {
            const item = this.refs.item as HTMLElement | undefined;

            if (item && !this.props.useInlineNesting) {
                const
                    bounds = item.getBoundingClientRect(),
                    oldBounds = this.state.expandPanelBounds;

                let parent = item.parentElement;

                while (parent && !parent?.className.includes('content')) {
                    parent = parent.parentElement;
                }

                const
                    parentBounds = parent?.getBoundingClientRect(),
                    panelBounds = {
                        top: bounds.top + (document.scrollingElement || document.documentElement).scrollTop,
                        left: (parentBounds ? parentBounds.right : bounds.right) + parseInt(common.commonShadowBlur) - parseInt(common.borderWidthDefault),
                        maxHeight: window.innerHeight - (bounds.top + (document.scrollingElement || document.documentElement).scrollTop) - parseInt(common.commonVerticalMargin)
                    },

                    expandPanel: HTMLElement | undefined = (this.refs['expand-panel'] as any)?.container,
                    expandPanelBounds = (expandPanel?.getElementsByClassName('content')[0] as HTMLElement | undefined)?.getBoundingClientRect();

                if (expandPanelBounds && expandPanelBounds.right > window.innerWidth) {
                    this.props.onSetMaxNesting(this, this.props.depth);
                    this.setState({ expandPanelBounds: null });
                }
                else if (oldBounds?.top !== panelBounds.top || oldBounds?.left !== panelBounds.left || oldBounds?.maxHeight !== panelBounds.maxHeight) {
                    this.setState({ expandPanelBounds: panelBounds });
                }
            }
        }
        else if (this.state.expandPanelBounds) {
            this.setState({ expandPanelBounds: null });
        }
    }

    componentWillUnmount() {
        this._clearTimeouts();
    }

    _hoverTimeoutID: ReturnType<typeof setTimeout> | null = null;
    _unhoverTimeoutID: ReturnType<typeof setTimeout> | null = null;

    private _clearTimeouts() {
        if (this._hoverTimeoutID) {
            clearTimeout(this._hoverTimeoutID);
            this._hoverTimeoutID = null;
        }

        if (this._unhoverTimeoutID) {
            clearTimeout(this._unhoverTimeoutID);
            this._unhoverTimeoutID = null;
        }
    }


    private _onMouseOver: ReactEventHandler = (event) => {
        const item = this.refs.item as HTMLElement | undefined;
        if (this.props.collapsable && !this.props.useInlineNesting && item && isOverEvent(event as unknown as MouseEvent, item)) {
            event.stopPropagation();
            event.preventDefault();

            if (!this.props.expanded) {
                this._onExpand(event as unknown as any);
            }
        }
    };


    private _onExpand: ReactEventHandler = () => {
        this._clearTimeouts();

        if (this.props.onExpand) { this.props.onExpand(this); }
        if (this.props.onItemExpand) { this.props.onItemExpand(this, this.props.id); }
    };


    private _onPopupMouseOver: ReactEventHandler = (event) => {
        if (this.props.expanded) {
            this._clearTimeouts();

            event.stopPropagation();
            event.preventDefault();
        }
    };

    private _onPopupMouseOut = () => {
        if (this.props.onSecondaryDropdownClose) {
            if (this._unhoverTimeoutID) { clearTimeout(this._unhoverTimeoutID); }
            this._unhoverTimeoutID = setTimeout(() => this.props.onSecondaryDropdownClose(this), 200);
        }
    };


    render() {
        const
            { className, title, children, collapsable, expanded, depth, useInlineNesting } = this.props,
            { expandPanelBounds } = this.state,
            padding = parseInt(common.commonHorizontalPadding) * 2 * depth + parseInt(common.commonHorizontalPadding);

        return (
            <div className={`nested-menu-item${expanded ? ' expanded' : ''}${className ? ` ${className}` : ''}`}>
                <div ref="item" className="item" style={{ paddingLeft: `${padding}px` }} onMouseOver={this._onMouseOver}>
                    <span className="title">{isResource(title) ? <FormattedResource resource={title} /> : title}</span>
                    {
                        collapsable && useInlineNesting ?
                            <button className="inline" onClick={this._onExpand}>
                                <span className={`fa fa-${!expanded ? 'plus' : 'minus'}-square-o`} />
                            </button> :
                            null
                    }
                </div>

                {!collapsable || useInlineNesting ? children : null}
                {
                    collapsable && expandPanelBounds && !useInlineNesting ?
                        <Popup
                            ref="expand-panel"
                            className={
                                `nested-menu-dropdown nested-menu-secondary-dropdown nested-menu-secondary-dropdown-${this.props.componentIdentifier}${
                                    className ? ` ${className.replace(/(\S+)/g, '$1-secondary-dropdown')}` : ''}`
                            }
                            fixed={false}
                            top={expandPanelBounds.top}
                            left={expandPanelBounds.left}
                        >
                            <div
                                className="child-panel"
                                style={{ maxHeight: `${expandPanelBounds.maxHeight}px` }}
                                onMouseOver={this._onPopupMouseOver}
                                onMouseOut={this._onPopupMouseOut}
                            >
                                {children}
                            </div>
                        </Popup> :
                        null
                }
            </div>
        );
    }
}


const
    isImpossibleChild = (_child: ReactElement): _child is ReactElement => false,
    isItem = (child: ReactElement): child is ReactElement<ItemProps> => !!child && typeof child === 'object' && child.type === Item;

function keyify(child: Child, groupKey: string): { key: string; child: Child; };
function keyify(child: Child[], groupKey: string): { key: string; child: Child; }[];
function keyify(child: Child | Child[], groupKey: string): { key: string; child: Child; } | { key: string; child: Child; }[] {
    return Array.isArray(child) ?
        child.map((grandChild, grandChildIndex) => keyify(grandChild, `${groupKey}.${grandChildIndex}`)) :
        {
            key: child?.key ? `external-${child.key}` : `index-${groupKey}`,
            child
        };
}

interface Props {
    className?: string;
    title: ReactElement | Resource;
    icon?: string | null;
    children?: Children;
    disabled?: boolean;
    opened?: boolean;
    onOpen?: ComponentEventHandler;
    onClose: ComponentEventHandler;
}

interface State {
    opened: boolean;
    expandedItems: (string | number)[];
    maxDepthLevel: number | null;
}

export default class NestedMenu extends PureComponent<Props, State> {
    static readonly Item = Item;

    static readonly propTypes = {
        className: PropTypes.string,
        title: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        icon: PropTypes.string,
        disabled: PropTypes.bool,
        opened: PropTypes.bool,
        onOpen: PropTypes.func,
        onClose: PropTypes.func
    };

    static readonly defaultProps = {
        className: ''
    };

    state: State = {
        opened: false,
        expandedItems: [],
        maxDepthLevel: null
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.opened !== undefined) {
            this.setState({
                opened: nextProps.opened,
                expandedItems: nextProps.opened ? this.state.expandedItems : []
            });
        }
    }

    componentDidMount() {
        this.componentDidUpdate();
    }

    componentDidUpdate() {
        if (this.state.opened) {
            if (!this._listeningMouseMove) { addEvent(window, 'mousemove', this._onMouseMove.bind(this)); }
        }
        else if (this._listeningMouseMove) {
            removeEvent(window, 'mousemove', this._onMouseMove.bind(this));
        }
    }

    componentWillUnmount() {
        if (this._listeningMouseMove) { removeEvent(window, 'mousemove', this._onMouseMove.bind(this)); }
    }

    private _uniqueIdentifier = Math.random() * Number.MAX_SAFE_INTEGER | 0;
    private _listeningMouseMove = false;
    private _mouseX = 0;
    private _mouseY = 0;


    private _onMouseMove: ReactEventHandler<MouseEvent> = (event) => {
        this._mouseX = event.clientX;
        this._mouseY = event.clientY;
    };


    private _onOpen = (openable: boolean) => {
        if (openable) {
            if (this.props.onOpen) { this.props.onOpen(this); }
            this.setState({ opened: true });
        }
    };

    private _onClose: ComponentEventHandler = () => {
        const
            mainDropdowns = document.getElementsByClassName(`nested-menu-${this._uniqueIdentifier}-dropdown`),
            secondaryDropdowns = document.getElementsByClassName(`nested-menu-secondary-dropdown-${this._uniqueIdentifier}`);

        for (let dropdownIndex = 0; dropdownIndex < mainDropdowns.length || dropdownIndex < secondaryDropdowns.length; dropdownIndex++) {
            const
                mainDropdown = mainDropdowns[dropdownIndex],
                secondaryDropdown = secondaryDropdowns[dropdownIndex];

            if (mainDropdown) {
                const bounds = mainDropdown.getBoundingClientRect();
                if (this._mouseX >= bounds.left && this._mouseX <= bounds.right && this._mouseY >= bounds.top && this._mouseY <= bounds.bottom) { return; }
            }

            if (secondaryDropdown) {
                const bounds = secondaryDropdown.getBoundingClientRect();
                if (this._mouseX >= bounds.left && this._mouseX <= bounds.right && this._mouseY >= bounds.top && this._mouseY <= bounds.bottom) { return; }
            }
        }

        if (this.props.onClose) {
            this.props.onClose(this);
        }
        else {
            this.setState({
                opened: false,
                expandedItems: []
            });
        }
    };


    private _onItemExpand: ComponentEventHandler1<string | number> = (_sender, key) => {
        this.setState({ expandedItems: this.state.expandedItems.copy().toggle(key) });
    };

    private _onSetMaxNesting: ComponentEventHandler1<number> = (_sender, depth) => {
        this.setState({ maxDepthLevel: this.state.maxDepthLevel !== null ? Math.min(this.state.maxDepthLevel, depth) : depth });
    };


    private _renderChildren(depth = 0, children: Children = this.props.children, iteration = 0): ReactNode[] {
        const { maxDepthLevel } = this.state;

        return toArray(children || [])
            .map((child, index) => keyify(child as any as Child[], `${iteration}${index}`))
            .flatten()
            .map(({ key, child }) => {
                if (!child || isImpossibleChild(child) || !isItem(child)) { return null; }

                const
                    { children: grandChildren, expanded, ...grandChildProps } = child.props,
                    useInlineNesting = !child.props.collapsable || maxDepthLevel !== null && maxDepthLevel <= depth,
                    isItemExpanded = expanded === undefined ? this.state.expandedItems.some(itemKey => itemKey === key) : expanded;

                return (
                    <InternalItem
                        key={key}
                        componentIdentifier={this._uniqueIdentifier}
                        id={key}
                        depth={depth}
                        expanded={isItemExpanded}
                        useInlineNesting={useInlineNesting}
                        onItemExpand={this._onItemExpand}
                        onSetMaxNesting={this._onSetMaxNesting}
                        onSecondaryDropdownClose={this._onClose}
                        {...grandChildProps}
                    >
                        {
                            !child.props.collapsable || isItemExpanded ?
                                this._renderChildren(useInlineNesting ? depth + 1 : 0, grandChildren || [], iteration + 1) as any[] :
                                []
                        }
                    </InternalItem>
                );
            })
            .filter(v => v);
    }

    render() {
        const
            { title } = this.props,
            { opened } = this.state,
            children = this._renderChildren(),
            hasChildren = children.length > 0;

        return (
            <DropPanel
                className={`nested-menu nested-menu-${this._uniqueIdentifier} ${this.props.className ? ` ${this.props.className}` : ''}`}
                title={title}
                icon={this.props.icon !== undefined ? this.props.icon : !hasChildren ? null : `chevron-${opened ? 'up' : 'down'}`}
                arrowed
                snap={[DropPanel.SnapSide.LEFT]}
                disabled={this.props.disabled}
                openMode={[DropPanel.CloseMode.BUTTON, DropPanel.OpenMode.HOVER]}
                closeMode={[DropPanel.CloseMode.BUTTON, DropPanel.CloseMode.OUTSIDE, DropPanel.CloseMode.UNHOVER]}
                hoverTimeout={0}
                opened={opened}
                onOpen={this._onOpen.bind(this, hasChildren)}
                onClose={this._onClose}
            >
                {children}
            </DropPanel>
        );
    }
}
